/* ═══════════════════════════════════════════
   Homepage translations — EN & ES
   Single source of truth for all homepage copy.
   ═══════════════════════════════════════════ */

export const translations = {
  en: {
    lang: 'en',
    // Header
    header: {
      requestBriefing: 'Request Briefing',
      signIn: 'Sign In',
      getStarted: 'Get Started',
    },
    // Hero
    hero: {
      badge: 'Brand Intelligence for Soccer',
      headline: "Brand is one of soccer\u2019s most undervalued",
      headlineAccent: ' commercial assets.',
      subtitle: 'YBV helps clubs, federations, and rights holders measure brand value, uncover commercial upside, and make stronger sponsorship, partnership, and growth decisions.',
      ctaPrimary: 'Request a Briefing',
      ctaSecondary: 'Explore Club Demo',
      signInPrompt: 'Already have access? Sign in',
    },
    // Problem
    problem: {
      eyebrow: 'The Commercial Blind Spot',
      headline: 'On the pitch, soccer measures everything.',
      headlineAccent: ' Off the pitch, brand value is still largely unmanaged.',
      bullets: [
        'Commercial decisions made without a clear view of brand value',
        'Brand signals fragmented across teams, markets, and partners',
        'Untapped sponsorship and growth opportunities left unseen',
      ],
    },
    // Solution — 3 pillars
    solution: {
      eyebrow: 'How It Works',
      headline: 'Measure. Understand. Decide.',
      cards: [
        {
          title: 'Measure',
          desc: 'Track brand value continuously across key drivers, directional shifts, and market signals.',
        },
        {
          title: 'Understand',
          desc: 'See what drives value, where it erodes, and how your position compares to peers.',
        },
        {
          title: 'Decide',
          desc: 'Turn intelligence into sharper sponsorship, partnership, and strategic decisions.',
        },
      ],
      cta: 'See It in Action',
    },
    // Deliverables
    deliverables: {
      eyebrow: 'What Your Team Receives',
      headline: 'Commercial intelligence your team can act on',
      items: [
        { title: 'Executive Dashboard', desc: 'A live intelligence workspace for commercial and leadership teams.' },
        { title: 'Commercial Benchmarking', desc: 'See how your brand position compares across peers, categories, and markets.' },
        { title: 'Sponsorship Opportunities', desc: 'Spot whitespace, unactivated categories, and areas of commercial upside.' },
        { title: 'Board-Ready Decision Support', desc: 'Access scenarios, exports, and strategic outputs built for confident decision-making.' },
      ],
      cta: 'Request a Briefing',
    },
    // Trust
    trust: {
      eyebrow: 'Why Trust YBV',
      headline: 'Enterprise-grade by design. Soccer-specific by nature.',
      items: [
        { title: 'Built for soccer organizations', desc: 'Designed for clubs, federations, and rights holders with complex commercial environments.' },
        { title: 'Enterprise-ready architecture', desc: 'Role-based access, tenant separation, and procurement-ready documentation.' },
        { title: 'Secure data environments', desc: 'Confidential exports, controlled integrations, and privacy-conscious workflows.' },
        { title: 'Recognised methodology', desc: 'Grounded in established brand valuation and commercial intelligence principles.' },
      ],
    },
    // Final CTA
    finalCta: {
      headline: 'See your brand',
      headlineAccent: ' like an asset.',
      subtitle: 'Start with a briefing. We\u2019ll show you where brand value is being lost, where commercial upside exists, and how a working platform could be deployed fast.',
      ctaPrimary: 'Request a Briefing',
      ctaSecondary: 'Explore Demo',
    },
    // Footer
    footer: {
      tagline: 'A soccer intelligence platform for measuring brand value and turning it into stronger commercial decisions.',
      platformTitle: 'Platform',
      requestBriefing: 'Request Briefing',
      clubDemo: 'Club Demo',
      wcbi: 'World Cup Brand Index™',
      signIn: 'Sign In',
      legalTitle: 'Legal',
      privacy: 'Privacy Policy',
      terms: 'Terms & Conditions',
      legalNotice: 'Legal Notice',
      contactTitle: 'Contact',
      disclaimer: 'All valuations and projections are directional estimates based on public data signals and proprietary analytical models. They do not constitute financial advice or certified appraisals. Outputs are intended for internal strategic planning within authorized recipient organizations.',
      copyright: 'YBV Brand Intelligence Platform. All rights reserved.',
    },
  },

  es: {
    lang: 'es',
    // Header
    header: {
      requestBriefing: 'Solicitar Briefing',
      signIn: 'Iniciar Sesi\u00f3n',
      getStarted: 'Comenzar',
    },
    // Hero
    hero: {
      badge: 'Inteligencia de Marca para el F\u00fatbol',
      headline: 'La marca es uno de los activos comerciales',
      headlineAccent: ' m\u00e1s infravalorados del f\u00fatbol.',
      subtitle: 'YBV ayuda a clubes, federaciones y titulares de derechos a medir el valor de marca, descubrir potencial comercial y tomar mejores decisiones de patrocinio, partnerships y crecimiento.',
      ctaPrimary: 'Solicitar Briefing',
      ctaSecondary: 'Explorar Demo de Clubes',
      signInPrompt: '\u00bfYa tienes acceso? Iniciar sesi\u00f3n',
    },
    // Problem
    problem: {
      eyebrow: 'El Punto Ciego Comercial',
      headline: 'En el campo, el f\u00fatbol lo mide todo.',
      headlineAccent: ' Fuera del campo, el valor de marca sigue sin gestionarse adecuadamente.',
      bullets: [
        'Decisiones comerciales sin una visi\u00f3n clara del valor de marca',
        'Se\u00f1ales de marca fragmentadas entre equipos, mercados y partners',
        'Oportunidades de patrocinio y crecimiento sin detectar',
      ],
    },
    // Solution — 3 pillars
    solution: {
      eyebrow: 'C\u00f3mo Funciona',
      headline: 'Medir. Entender. Decidir.',
      cards: [
        {
          title: 'Medir',
          desc: 'Seguimiento continuo del valor de marca a trav\u00e9s de factores clave, cambios direccionales y se\u00f1ales de mercado.',
        },
        {
          title: 'Entender',
          desc: 'Visualiza qu\u00e9 impulsa el valor, d\u00f3nde se erosiona y c\u00f3mo se compara tu posici\u00f3n con los competidores.',
        },
        {
          title: 'Decidir',
          desc: 'Transforma la inteligencia en decisiones m\u00e1s precisas de patrocinio, partnerships y estrategia.',
        },
      ],
      cta: 'Ver en Acci\u00f3n',
    },
    // Deliverables
    deliverables: {
      eyebrow: 'Qu\u00e9 Recibe Tu Equipo',
      headline: 'Inteligencia comercial sobre la que tu equipo puede actuar',
      items: [
        { title: 'Dashboard Ejecutivo', desc: 'Un espacio de trabajo de inteligencia en tiempo real para equipos comerciales y de direcci\u00f3n.' },
        { title: 'Benchmarking Comercial', desc: 'Compara la posici\u00f3n de tu marca frente a competidores, por categor\u00eda y mercado.' },
        { title: 'Oportunidades de Patrocinio', desc: 'Detecta espacios sin activar, categor\u00edas disponibles y \u00e1reas de potencial comercial.' },
        { title: 'Soporte para Decisiones del Consejo', desc: 'Accede a escenarios, exportaciones y outputs estrat\u00e9gicos dise\u00f1ados para la toma de decisiones con confianza.' },
      ],
      cta: 'Solicitar Briefing',
    },
    // Trust
    trust: {
      eyebrow: 'Por Qu\u00e9 Confiar en YBV',
      headline: 'Arquitectura enterprise por dise\u00f1o. Espec\u00edfica del f\u00fatbol por naturaleza.',
      items: [
        { title: 'Dise\u00f1ada para organizaciones de f\u00fatbol', desc: 'Construida para clubes, federaciones y titulares de derechos con entornos comerciales complejos.' },
        { title: 'Arquitectura enterprise-ready', desc: 'Acceso por roles, separaci\u00f3n por tenant y documentaci\u00f3n preparada para procurement.' },
        { title: 'Entornos de datos seguros', desc: 'Exportaciones confidenciales, integraciones controladas y flujos de trabajo respetuosos con la privacidad.' },
        { title: 'Metodolog\u00eda reconocida', desc: 'Basada en principios establecidos de valoraci\u00f3n de marca e inteligencia comercial.' },
      ],
    },
    // Final CTA
    finalCta: {
      headline: 'Gestiona tu marca',
      headlineAccent: ' como un activo.',
      subtitle: 'Comienza con un briefing. Te mostraremos d\u00f3nde se pierde valor de marca, d\u00f3nde existe potencial comercial y c\u00f3mo una plataforma operativa podr\u00eda desplegarse r\u00e1pidamente.',
      ctaPrimary: 'Solicitar Briefing',
      ctaSecondary: 'Explorar Demo',
    },
    // Footer
    footer: {
      tagline: 'Una plataforma de inteligencia para el f\u00fatbol que mide el valor de marca y lo transforma en mejores decisiones comerciales.',
      platformTitle: 'Plataforma',
      requestBriefing: 'Solicitar Briefing',
      clubDemo: 'Demo de Clubes',
      wcbi: 'World Cup Brand Index™',
      signIn: 'Iniciar Sesi\u00f3n',
      legalTitle: 'Legal',
      privacy: 'Pol\u00edtica de Privacidad',
      terms: 'T\u00e9rminos y Condiciones',
      legalNotice: 'Aviso Legal',
      contactTitle: 'Contacto',
      disclaimer: 'Todas las valoraciones y proyecciones son estimaciones direccionales basadas en se\u00f1ales de datos p\u00fablicos y modelos anal\u00edticos propietarios. No constituyen asesoramiento financiero ni tasaciones certificadas. Los outputs est\u00e1n destinados a la planificaci\u00f3n estrat\u00e9gica interna de las organizaciones autorizadas.',
      copyright: 'YBV Brand Intelligence Platform. Todos los derechos reservados.',
    },
  },
};
